import React from 'react';
import { Server, Code, Laptop, Shield, AlertTriangle } from 'lucide-react';

interface CmsInfoBoxProps {
  cmsData: {
    name: string;
    version?: string;
    confidence?: number;
    description?: string;
    logo_url?: string;
  } | null;
}

const CmsInfoBox: React.FC<CmsInfoBoxProps> = ({ cmsData }) => {
  // Si aucune donnée n'est fournie, afficher un état de chargement
  if (!cmsData) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center">
            <Server className="w-4 h-4 mr-2 text-blue-600" />
            Information CMS
          </h3>
        </div>
        <div className="p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center mr-3 animate-pulse">
            </div>
            <div>
              <div className="h-4 bg-gray-200 rounded w-24 mb-2 animate-pulse"></div>
              <div className="h-3 bg-gray-100 rounded w-16 animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Définir la couleur de badge en fonction du CMS
  const getBadgeColor = () => {
    if (cmsData.name === 'WordPress') return 'bg-blue-500';
    if (cmsData.name === 'PrestaShop') return 'bg-purple-500';
    return 'bg-gray-500';
  };

  // Définir l'icône en fonction du CMS (utilisant des classes)
  const getCmsIconClass = () => {
    if (cmsData.name === 'WordPress') return 'fab fa-wordpress';
    if (cmsData.name === 'PrestaShop') return 'fas fa-shopping-cart';
    if (cmsData.name === 'Site développé sur mesure') return 'fas fa-laptop-code';
    return 'fas fa-code';
  };

  // Obtenir le composant Lucide React en fonction du CMS
  const getCmsLucideIcon = () => {
    if (cmsData.name === 'WordPress') return null; // On utilise Font Awesome pour WordPress
    if (cmsData.name === 'PrestaShop') return null; // On utilise Font Awesome pour PrestaShop
    if (cmsData.name === 'Site développé sur mesure') return <Laptop className="w-8 h-8" />;
    return <Code className="w-8 h-8" />;
  };

  // Obtenir le texte descriptif compact en fonction du CMS
  const getCmsDescription = () => {
    if (cmsData.name === 'WordPress') {
      const versionText = cmsData.version ? `WordPress ${cmsData.version}` : 'WordPress';
      return {
        main: `Site sous ${versionText}, CMS populaire et puissant.`,
        warning: 'Attention aux plugins non mis à jour et à la sécurité.',
        tips: ['Mises à jour régulières', 'Sauvegardes automatiques', 'Sécurisation admin']
      };
    }
    
    if (cmsData.name === 'PrestaShop') {
      return {
        main: cmsData.version 
          ? `Boutique PrestaShop ${cmsData.version}, solution e-commerce complète.`
          : 'Boutique PrestaShop, solution e-commerce complète.',
        warning: null,
        tips: []
      };
    }
    
    return {
      main: cmsData.version
        ? `Site utilisant ${cmsData.name} ${cmsData.version}.`
        : `Site utilisant ${cmsData.name}.`,
      warning: null,
      tips: []
    };
  };

  const description = getCmsDescription();

  return (
    <div className="space-y-4">
      {/* Header compact */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-indigo-50 text-indigo-700">
            <Server className="w-4 h-4" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800">Information CMS</h3>
        </div>
        {cmsData.confidence && (
          <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
            {cmsData.confidence}%
          </span>
        )}
      </div>

      {/* CMS Info compact */}
      <div className="flex items-center space-x-4">
        {cmsData.logo_url ? (
          <div className="w-12 h-12 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
            <img 
              src={cmsData.logo_url} 
              alt={`Logo ${cmsData.name}`} 
              className="w-full h-full object-contain p-1"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
                const parent = (e.target as HTMLImageElement).parentNode as HTMLElement;
                if (parent) {
                  parent.classList.add(getBadgeColor(), 'text-white');
                  const fallbackEl = document.createElement('i');
                  fallbackEl.className = getCmsIconClass();
                  parent.appendChild(fallbackEl);
                }
              }}
            />
          </div>
        ) : (
          <div className={`w-12 h-12 ${getBadgeColor()} text-white flex items-center justify-center rounded-lg flex-shrink-0`}>
            {getCmsLucideIcon() || (
              <i className={`${getCmsIconClass()} text-xl`}></i>
            )}
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <h4 className="text-base font-medium text-gray-800 truncate">{cmsData.name}</h4>
          <p className="text-sm text-gray-500">
            {cmsData.version ? `Version ${cmsData.version}` : 'Version non détectée'}
          </p>
        </div>
      </div>
        
      {/* Description compacte */}
      <div className="space-y-3">
        <p className="text-sm text-gray-600">{description.main}</p>
        
        {description.warning && (
          <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-800">{description.warning}</p>
          </div>
        )}
        
        {description.tips.length > 0 && (
          <div className="flex items-start space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <Shield className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-green-800">
              <p className="font-medium mb-1">Recommandations :</p>
              <ul className="space-y-1">
                {description.tips.map((tip, index) => (
                  <li key={index} className="flex items-center space-x-1">
                    <span className="w-1 h-1 bg-green-600 rounded-full"></span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CmsInfoBox;
